import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import Header from "./Header";
import { URLS } from "../Url";
import { mediaSrc } from "../utils/media";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";

const DEFAULT_VENUES = [
  {
    name: "Gachibowli",
    city: "Hyderabad",
    landmark: "Behind Indriya jewelers",
    addressLine1: "https://maps.app.goo.gl/KYY6HgE3JeCoeTwx5",
  },
  {
    name: "Kondapur - Premium Theatres",
    city: "Hyderabad",
    landmark: "Asian Suncity Behind Sarath City Mall",
    addressLine1: "https://maps.app.goo.gl/pVvS2FTLun3fKMXs5",
  },
  {
    name: "Miyapur",
    city: "Hyderabad",
    landmark: "Beside Kalyan Jewellers",
    addressLine1: "3rd Floor Above Bajaj Bike showroom",
    addressLine2: "https://maps.app.goo.gl/s64nEX9Crtqr7MyN7",
  },
];

const getMapUrl = (addr) => {
  if (addr.addressLine1 && typeof addr.addressLine1 === "string" && addr.addressLine1.startsWith("http")) {
    return addr.addressLine1.trim();
  }
  if (addr.addressLine2 && typeof addr.addressLine2 === "string" && addr.addressLine2.startsWith("http")) {
    return addr.addressLine2.trim();
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `Carnival Castle ${addr.name}, ${addr.city || "Hyderabad"}`
  )}`;
};

const getHumanReadableAddress = (addr) => {
  const parts = [];
  if (addr.addressLine1 && typeof addr.addressLine1 === "string" && !addr.addressLine1.startsWith("http")) {
    parts.push(addr.addressLine1.trim());
  }
  if (addr.addressLine2 && typeof addr.addressLine2 === "string" && !addr.addressLine2.startsWith("http")) {
    parts.push(addr.addressLine2.trim());
  }
  if (addr.landmark && typeof addr.landmark === "string" && !addr.landmark.startsWith("http")) {
    parts.push(addr.landmark.trim());
  }
  return parts.length > 0 ? parts.join(" · ") : null;
};

const formatLocationName = (name) => {
  if (!name) return "";
  return name.replace(/\s*-\s*/g, " — ");
};

export default function About() {
  const navigate = useNavigate();
  const [about, setAbout] = useState({});
  const [features, setFeatures] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  const BaseUrl = "https://api.carnivalcastle.com/v1/carnivalApi/";

  useEffect(() => {
    const promises = [
      axios
        .post(URLS.GetAbout, {})
        .then((r) => {
          if (r.status === 200 && r.data?.aboutus) {
            setAbout(r.data.aboutus);
          }
        })
        .catch(() => {}),
      axios
        .post(URLS.GetAboutFeatures, {})
        .then((r) => {
          if (r.status === 200 && r.data?.features) {
            setFeatures(r.data.features);
          }
        })
        .catch(() => {}),
      axios
        .get(`${BaseUrl}admin/address/alladdress`)
        .then((r) => {
          if (r.data?.success && r.data?.data) {
            setAddresses(r.data.data);
          }
        })
        .catch(() => {}),
    ];

    Promise.all(promises).finally(() => setLoading(false));
  }, []);

  const CREATIONS = [
    {
      num: "01",
      title: "Private Theatre",
      desc: "An exclusive 4K projection hall reserved entirely for your guests with concert-grade surround sound and plush lounge seating.",
    },
    {
      num: "02",
      title: "Personalised Décor",
      desc: "Bespoke celebration themes, ambient candle walkways, custom LED name installations, and fresh floral setups tailored to your occasion.",
    },
    {
      num: "03",
      title: "Celebration Dining",
      desc: "Artisanal celebration cakes, delectable refreshments, and curated party treats served right inside your private lounge.",
    },
    {
      num: "04",
      title: "Surprise Experiences",
      desc: "Magical cold fire entries, atmospheric floor fog, photo memory clip displays, and dedicated celebration photography.",
    },
  ];

  const DIFFERENTIATORS = [
    {
      keyword: "Private",
      tagline: "Your celebration belongs entirely to you.",
      detail: "No strangers, no interruptions, and no shared spaces. The entire venue is locked for your private gathering.",
    },
    {
      keyword: "Personalised",
      tagline: "Every detail can be shaped around your occasion.",
      detail: "From custom LED name lights and personalized video screenings to specific color themes and cake flavors.",
    },
    {
      keyword: "Cinematic",
      tagline: "A theatre experience designed to make the moment feel larger than life.",
      detail: "Immersive big-screen visual storytelling that elevates ordinary celebrations into cinematic spectacles.",
    },
    {
      keyword: "Memorable",
      tagline: "More than a booking — an experience worth remembering.",
      detail: "Every milestone is captured with precision, emotion, and heartfelt attention from our dedicated celebration hosts.",
    },
  ];

  const STATS = [
    { value: "03", label: "Hyderabad Locations" },
    { value: "50K+", label: "Celebrations Hosted" },
    { value: "4.9★", label: "Guest Satisfaction" },
    { value: "15+", label: "Signature Décor Themes" },
  ];

  return (
    <div className="ab-page-root" style={{ backgroundColor: "#FAF7F2", minHeight: "100vh" }}>
      <Helmet>
        <title>About Carnival Castle | Hyderabad's Premier Private Theatre</title>
        <meta
          name="description"
          content="Carnival Castle transforms ordinary celebrations into private cinematic experiences with bespoke décor, food, and surprises."
        />
      </Helmet>

      <Header />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,400;1,600&family=DM+Sans:wght@400;500;600;700&display=swap');

        /* ── Global Page Tokens ── */
        .ab-page-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #FAF7F2;
          background-color: #0E0217;
          line-height: 1.6;
        }

        .ab-serif {
          font-family: 'Playfair Display', Georgia, serif;
        }

        .ab-container {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .ab-container-narrow {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 24px;
        }



        /* ── 2. Split Experience Section: Our Story ── */
        .ab-story-sec {
          background-color: #FAF7F2;
          color: #1A032B;
          padding: 34px 0 52px;
        }

        .ab-story-sec .ab-container {
          max-width: 1320px;
          padding: 0 28px;
        }

        .ab-story-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 52px;
          align-items: stretch;
        }

        .ab-story-visual-wrap {
          position: relative;
          height: 100%;
          display: flex;
        }

        .ab-story-frame {
          position: relative;
          border-radius: 18px;
          overflow: hidden;
          width: 100%;
          height: 100%;
          box-shadow: 0 8px 24px rgba(26, 3, 43, 0.07);
          display: flex;
        }

        .ab-story-img {
          width: 100%;
          height: 100%;
          flex: 1;
          object-fit: cover;
          object-position: center center;
          display: block;
          transition: transform 0.4s ease;
        }

        .ab-story-frame:hover .ab-story-img {
          transform: scale(1.02);
        }

        .ab-story-caption {
          position: absolute;
          bottom: 14px;
          left: 14px;
          background-color: rgba(24, 4, 38, 0.82);
          backdrop-filter: blur(6px);
          color: #FAF7F2;
          padding: 8px 14px;
          border-radius: 6px;
          border: 1px solid rgba(201, 168, 76, 0.3);
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          pointer-events: none;
        }

        .ab-story-content {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .ab-story-tag {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #8A6D24;
          margin-top: 0;
          margin-bottom: 10px;
          line-height: 1.2;
        }

        .ab-story-heading {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(28px, 3.2vw, 40px);
          font-weight: 600;
          line-height: 1.22;
          color: #1A032B;
          margin-bottom: 18px;
          letter-spacing: -0.3px;
        }

        .ab-story-p {
          font-size: 15px;
          line-height: 1.7;
          color: #4A3556;
          margin-bottom: 16px;
        }

        .ab-story-highlight {
          border-left: 2px solid #C9A84C;
          padding-left: 18px;
          margin: 18px 0 24px;
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          font-size: 16px;
          color: #1A032B;
          line-height: 1.5;
        }

        .ab-story-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: #1A032B;
          color: #FAF7F2;
          border: 1px solid #1A032B;
          padding: 12px 26px;
          border-radius: 6px;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.22s ease;
        }

        .ab-story-btn:hover {
          background-color: #380C55;
          border-color: #380C55;
          color: #FFFFFF;
        }

        /* ── 3. What We Create: Numbered Editorial List ── */
        .ab-creations-sec {
          background-color: #FFFDF9;
          color: #1A032B;
          padding: 104px 0 114px;
          border-top: 1px solid #EAE1D5;
          border-bottom: 1px solid #EAE1D5;
        }

        .ab-section-header {
          text-align: center;
          margin-bottom: 64px;
        }

        .ab-sec-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #8A6D24;
          margin-bottom: 12px;
        }

        .ab-sec-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 600;
          color: #1A032B;
          margin-bottom: 14px;
          letter-spacing: -0.2px;
        }

        .ab-sec-subtitle {
          font-size: 15px;
          color: #6C5578;
          max-width: 540px;
          margin: 0 auto;
        }

        .ab-creations-list {
          border-top: 1px solid #E6DED2;
        }

        .ab-creation-item {
          display: grid;
          grid-template-columns: 80px 240px 1fr 40px;
          align-items: center;
          padding: 32px 18px;
          border-bottom: 1px solid #E6DED2;
          transition: background-color 0.22s ease, padding-left 0.22s ease;
        }

        .ab-creation-item:hover {
          background-color: #F7F1E7;
          padding-left: 26px;
        }

        .ab-creation-num {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 20px;
          font-weight: 600;
          color: #C9A84C;
        }

        .ab-creation-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 600;
          color: #1A032B;
          letter-spacing: -0.2px;
        }

        .ab-creation-desc {
          font-size: 14.5px;
          line-height: 1.6;
          color: #4A3556;
          padding-right: 24px;
        }

        .ab-creation-arrow {
          color: #8A6D24;
          font-size: 18px;
          transition: transform 0.2s ease;
          text-align: right;
        }

        .ab-creation-item:hover .ab-creation-arrow {
          transform: translateX(4px);
        }

        /* ── 4. Why Carnival Castle Section ── */
        .ab-why-sec {
          background-color: #140322;
          color: #FAF7F2;
          padding: 104px 0 110px;
        }

        .ab-why-sec .ab-sec-eyebrow {
          color: #C9A84C;
        }

        .ab-why-sec .ab-sec-title {
          color: #FAF7F2;
        }

        .ab-why-sec .ab-sec-subtitle {
          color: #BCA8CD;
        }

        .ab-diff-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          margin-top: 56px;
        }

        .ab-diff-card {
          background-color: #180426;
          border: 1px solid rgba(201, 168, 76, 0.25);
          border-radius: 4px;
          padding: 36px 26px;
          display: flex;
          flex-direction: column;
          transition: transform 0.24s ease, border-color 0.24s ease;
        }

        .ab-diff-card:hover {
          transform: translateY(-4px);
          border-color: rgba(201, 168, 76, 0.6);
        }

        .ab-diff-star {
          color: #C9A84C;
          font-size: 14px;
          margin-bottom: 20px;
        }

        .ab-diff-keyword {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 24px;
          font-weight: 600;
          color: #FAF7F2;
          margin-bottom: 12px;
          letter-spacing: -0.2px;
        }

        .ab-diff-tagline {
          font-size: 13.5px;
          font-weight: 600;
          color: #C9A84C;
          margin-bottom: 14px;
          line-height: 1.45;
        }

        .ab-diff-divider {
          width: 32px;
          height: 1px;
          background-color: rgba(201, 168, 76, 0.3);
          margin-bottom: 14px;
        }

        .ab-diff-detail {
          font-size: 13px;
          line-height: 1.65;
          color: #BCA8CD;
        }

        /* ── 5. Brand Philosophy ── */
        .ab-philo-sec {
          background-color: #0E0217;
          border-top: 1px solid rgba(201, 168, 76, 0.15);
          border-bottom: 1px solid rgba(201, 168, 76, 0.15);
          padding: 96px 24px;
          text-align: center;
        }

        .ab-philo-quote {
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          font-size: clamp(30px, 4.5vw, 48px);
          font-weight: 500;
          color: #FAF7F2;
          line-height: 1.25;
          max-width: 820px;
          margin: 0 auto 24px;
          letter-spacing: -0.3px;
        }

        .ab-philo-text {
          font-size: 15.5px;
          line-height: 1.75;
          color: #BCA8CD;
          max-width: 640px;
          margin: 0 auto;
        }

        /* ── 6. Numbers / Scale Strip ── */
        .ab-stats-sec {
          background-color: #180426;
          border-bottom: 1px solid rgba(201, 168, 76, 0.2);
          padding: 64px 0;
        }

        .ab-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          text-align: center;
        }

        .ab-stat-item {
          padding: 0 16px;
          border-right: 1px solid rgba(201, 168, 76, 0.15);
        }

        .ab-stat-item:last-child {
          border-right: none;
        }

        .ab-stat-val {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(36px, 4.5vw, 48px);
          font-weight: 600;
          color: #C9A84C;
          line-height: 1;
          margin-bottom: 8px;
        }

        .ab-stat-lbl {
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #FAF7F2;
        }

        /* ── 7. Venues / Locations ── */
        .ab-venues-sec {
          background-color: #FAF7F2;
          color: #1A032B;
          padding: 104px 0 114px;
        }

        .ab-venues-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
          margin-top: 56px;
        }

        .ab-venue-card {
          background-color: #FFFDF9;
          border: 1px solid #E6DED2;
          border-radius: 6px;
          padding: 36px 30px 30px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
          transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1),
                      border-color 0.25s ease;
        }

        .ab-venue-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(26, 3, 43, 0.08);
          border-color: #C9A84C;
        }

        .ab-venue-card-featured {
          border: 1.5px solid #C9A84C;
          box-shadow: 0 8px 24px rgba(201, 168, 76, 0.12);
        }

        .ab-venue-card-featured::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background-color: #C9A84C;
        }

        .ab-venue-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .ab-venue-num-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ab-venue-num {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px;
          font-weight: 600;
          color: #8A6D24;
          letter-spacing: 0.5px;
        }

        .ab-venue-star {
          color: #C9A84C;
          font-size: 10px;
        }

        .ab-venue-pill {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          background-color: #F4ECE0;
          color: #8A6D24;
          padding: 4px 10px;
          border-radius: 3px;
          border: 1px solid #E6D9C8;
          white-space: nowrap;
        }

        .ab-venue-pill-gold {
          background-color: #C9A84C;
          color: #0E0217;
          border-color: #C9A84C;
        }

        .ab-venue-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 26px;
          font-weight: 600;
          line-height: 1.2;
          color: #1A032B;
          margin-bottom: 8px;
          letter-spacing: -0.2px;
        }

        .ab-venue-city {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: #8A6D24;
          margin-bottom: 16px;
        }

        .ab-venue-pin {
          color: #8A6D24;
          flex-shrink: 0;
        }

        .ab-venue-landmark {
          font-size: 13px;
          line-height: 1.5;
          color: #6C5578;
          margin-bottom: 12px;
        }

        .ab-venue-desc {
          font-size: 13.5px;
          line-height: 1.6;
          color: #4A3556;
          margin: 0;
        }

        .ab-venue-divider {
          width: 100%;
          height: 1px;
          background-color: #EFE5D2;
          margin: 24px 0 18px;
        }

        .ab-venue-cta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #1A032B;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-decoration: none;
          padding: 6px 0;
          transition: color 0.2s ease;
        }

        .ab-venue-cta:hover {
          color: #8A6D24;
        }

        .ab-venue-arrow {
          font-size: 16px;
          line-height: 1;
          color: #8A6D24;
          transition: transform 0.22s ease;
        }

        .ab-venue-cta:hover .ab-venue-arrow {
          transform: translateX(5px);
        }

        /* ── 8. Luxury Call-to-Action ── */
        .ab-cta-sec {
          background-color: #140322;
          color: #FAF7F2;
          border-top: 1px solid rgba(201, 168, 76, 0.2);
          padding: 96px 24px;
          text-align: center;
        }

        .ab-cta-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(30px, 4vw, 44px);
          font-weight: 600;
          color: #FAF7F2;
          margin-bottom: 16px;
          letter-spacing: -0.2px;
        }

        .ab-cta-desc {
          font-size: 15.5px;
          color: #BCA8CD;
          max-width: 540px;
          margin: 0 auto 36px;
        }

        .ab-cta-btns {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ab-btn-primary {
          background-color: #C9A84C;
          color: #0E0217;
          border: 1px solid #C9A84C;
          font-weight: 700;
          font-size: 14px;
          padding: 14px 32px;
          border-radius: 4px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .ab-btn-primary:hover {
          background-color: #D8BA60;
          border-color: #D8BA60;
          color: #0E0217;
        }

        .ab-btn-secondary {
          background-color: transparent;
          color: #FAF7F2;
          border: 1px solid rgba(250, 247, 242, 0.35);
          font-weight: 600;
          font-size: 14px;
          padding: 14px 28px;
          border-radius: 4px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .ab-btn-secondary:hover {
          background-color: rgba(250, 247, 242, 0.08);
          border-color: #FAF7F2;
          color: #FFFFFF;
        }

        /* ── Responsive Adaptations ── */
        @media (min-width: 768px) and (max-width: 991px) {
          .ab-story-grid {
            grid-template-columns: 1fr 1fr;
            gap: 28px;
            align-items: stretch;
          }
          .ab-story-heading {
            font-size: 26px;
            margin-bottom: 14px;
          }
          .ab-story-p {
            font-size: 14px;
            line-height: 1.6;
            margin-bottom: 12px;
          }
          .ab-story-highlight {
            font-size: 14px;
            margin: 12px 0 18px;
            padding-left: 14px;
          }
          .ab-story-frame {
            border-radius: 16px;
          }
        }

        @media (max-width: 991px) {
          .ab-story-visual-wrap {
            margin-top: 0;
            align-self: auto;
          }
          .ab-creation-item {
            grid-template-columns: 60px 180px 1fr 30px;
            padding: 24px 12px;
          }
          .ab-diff-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
          .ab-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            row-gap: 32px;
          }
          .ab-stat-item:nth-child(2) {
            border-right: none;
          }
          .ab-venues-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 22px;
          }
          .ab-venue-card:last-child {
            grid-column: span 2;
          }
        }

        @media (max-width: 767px) {
          .ab-story-sec {
            padding: 20px 0 36px;
          }
          .ab-story-sec .ab-container {
            padding: 0 16px;
          }
          .ab-story-grid {
            grid-template-columns: 1fr;
            gap: 22px;
          }
          .ab-story-frame {
            height: 260px;
            border-radius: 14px;
            min-height: 0;
          }
          .ab-story-img {
            height: 100%;
          }
          .ab-story-caption {
            bottom: 10px;
            left: 10px;
            font-size: 9.5px;
            padding: 6px 10px;
          }
          .ab-creations-sec, .ab-why-sec, .ab-venues-sec, .ab-philo-sec, .ab-cta-sec {
            padding: 64px 0;
          }
          .ab-creation-item {
            grid-template-columns: 1fr;
            gap: 8px;
            padding: 20px 8px;
          }
          .ab-creation-num {
            font-size: 15px;
          }
          .ab-creation-title {
            font-size: 18px;
          }
          .ab-creation-desc {
            font-size: 13.5px;
            padding-right: 0;
          }
          .ab-creation-arrow {
            display: none;
          }
          .ab-diff-grid {
            grid-template-columns: 1fr;
          }
          .ab-stats-grid {
            grid-template-columns: 1fr;
            row-gap: 28px;
          }
          .ab-stat-item {
            border-right: none;
            border-bottom: 1px solid rgba(201, 168, 76, 0.15);
            padding-bottom: 20px;
          }
          .ab-stat-item:last-child {
            border-bottom: none;
          }
          .ab-venues-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .ab-venue-card:last-child {
            grid-column: span 1;
          }
          .ab-venue-card {
            padding: 28px 22px 24px;
          }
          .ab-venue-name {
            font-size: 22px;
          }
        }
      `}</style>

      <main>
        {/* ── Our Story Section ── */}
        <section className="ab-story-sec">
          <div className="ab-container">
            <div className="ab-story-grid">
              {/* Left Column: Premium Visual */}
              <div className="ab-story-visual-wrap">
                <div className="ab-story-frame">
                  <img
                    src={about.image ? mediaSrc(about.image) : "/img/about.jpg"}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/img/about.jpg";
                    }}
                    alt="Carnival Castle Private Theatre Celebration"
                    className="ab-story-img"
                  />
                  <div className="ab-story-caption">
                    <span style={{ color: "#C9A84C" }}>✦</span>
                    <span>Hyderabad · Private Cinematic Celebrations</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Brand Story */}
              <div className="ab-story-content">
                <div className="ab-story-tag">01 · OUR STORY</div>
                <h2 className="ab-story-heading">We Turn Celebrations Into Cinema.</h2>

                <p className="ab-story-p">
                  At Carnival Castle, we believe life's most precious celebrations shouldn't be shared with a
                  room full of strangers or confined to a crowded restaurant table. We reimagined the theatre as
                  an intimate celebration sanctuary crafted exclusively for you and your inner circle.
                </p>

                <p className="ab-story-p">
                  {about.description ||
                    "Whether it is an intimate birthday surprise, a romantic anniversary screening, or a proposal planned down to the second, Carnival Castle transforms ordinary celebrations into private cinematic experiences with customized themes, immersive audio, and heartfelt hospitality."}
                </p>

                <div className="ab-story-highlight">
                  “Every celebration is directed around your story — framed in private 4K cinema and styled with warm ambient décor.”
                </div>

                <div>
                  <button
                    type="button"
                    className="ab-story-btn"
                    onClick={() => navigate("/locations")}
                  >
                    <span>Explore Locations</span>
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. What We Create: Refined Editorial List ── */}
        <section className="ab-creations-sec">
          <div className="ab-container">
            <div className="ab-section-header">
              <div className="ab-sec-eyebrow">02 · THE CRAFT</div>
              <h2 className="ab-sec-title">What We Create</h2>
              <p className="ab-sec-subtitle">
                Four essential elements woven into every Carnival Castle celebration experience.
              </p>
            </div>

            <div className="ab-creations-list">
              {CREATIONS.map((item) => (
                <div key={item.num} className="ab-creation-item">
                  <span className="ab-creation-num">{item.num}</span>
                  <span className="ab-creation-title">{item.title}</span>
                  <span className="ab-creation-desc">{item.desc}</span>
                  <span className="ab-creation-arrow">→</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. Why Carnival Castle Section ── */}
        <section className="ab-why-sec">
          <div className="ab-container">
            <div className="ab-section-header">
              <div className="ab-sec-eyebrow">03 · THE DISTINCTION</div>
              <h2 className="ab-sec-title">Why Carnival Castle?</h2>
              <p className="ab-sec-subtitle">
                A celebration format built around intimacy, precision, and theatrical grandeur.
              </p>
            </div>

            <div className="ab-diff-grid">
              {DIFFERENTIATORS.map((diff) => (
                <div key={diff.keyword} className="ab-diff-card">
                  <span className="ab-diff-star">✦</span>
                  <h3 className="ab-diff-keyword">{diff.keyword}</h3>
                  <div className="ab-diff-tagline">{diff.tagline}</div>
                  <div className="ab-diff-divider" />
                  <p className="ab-diff-detail">{diff.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 5. Brand Philosophy ── */}
        <section className="ab-philo-sec">
          <div className="ab-container-narrow">
            <span style={{ color: "#C9A84C", fontSize: 18, display: "block", marginBottom: 18 }}>✦</span>
            <blockquote className="ab-philo-quote">
              “Every celebration deserves its own stage.”
            </blockquote>
            <p className="ab-philo-text">
              We founded Carnival Castle on a simple conviction: the people you love deserve more than routine
              gatherings. By pairing private cinema technology with boutique event curation, we give you the
              freedom to laugh, cheer, celebrate, and reflect in absolute privacy.
            </p>
          </div>
        </section>

        {/* ── 6. Numbers / Scale Section ── */}
        <section className="ab-stats-sec">
          <div className="ab-container">
            <div className="ab-stats-grid">
              {STATS.map((stat) => (
                <div key={stat.label} className="ab-stat-item">
                  <div className="ab-stat-val">{stat.value}</div>
                  <div className="ab-stat-lbl">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. Venues & Locations ── */}
        <section className="ab-venues-sec">
          <div className="ab-container">
            <div className="ab-section-header">
              <div className="ab-sec-eyebrow">04 · HYDERABAD VENUES</div>
              <h2 className="ab-sec-title">Our Private Theatres</h2>
              <p className="ab-sec-subtitle">
                Curated venues positioned in prime neighborhoods across Hyderabad.
              </p>
            </div>

            <div className="ab-venues-grid">
              {(addresses.length > 0 ? addresses : DEFAULT_VENUES).map((addr, idx) => {
                const mapUrl = getMapUrl(addr);
                const readableAddress = getHumanReadableAddress(addr);
                const isFeatured = idx === 1; // Card 02 (Kondapur - Premium Theatres)
                const numStr = String(idx + 1).padStart(2, "0");

                return (
                  <article
                    key={addr._id || idx}
                    className={`ab-venue-card ${isFeatured ? "ab-venue-card-featured" : ""}`}
                  >
                    <div>
                      {/* Top Header */}
                      <div className="ab-venue-header">
                        <div className="ab-venue-num-wrap">
                          <span className="ab-venue-num">{numStr}</span>
                          {idx === 2 && <span className="ab-venue-star">✦</span>}
                        </div>
                        <span className={`ab-venue-pill ${isFeatured ? "ab-venue-pill-gold" : ""}`}>
                          {isFeatured ? "PREMIUM THEATRES" : "PRIVATE THEATRE"}
                        </span>
                      </div>

                      {/* Location Name */}
                      <h3 className="ab-venue-name">{formatLocationName(addr.name)}</h3>

                      {/* City with Pin */}
                      <div className="ab-venue-city">
                        <MapPin size={13} strokeWidth={2.2} className="ab-venue-pin" />
                        <span>{addr.city || "Hyderabad"}</span>
                      </div>

                      {/* Human-readable landmark / address */}
                      {readableAddress && (
                        <div className="ab-venue-landmark">{readableAddress}</div>
                      )}

                      {/* Description */}
                      <p className="ab-venue-desc">
                        Private celebration venue curated for memorable moments.
                      </p>
                    </div>

                    <div>
                      {/* Divider */}
                      <div className="ab-venue-divider" />

                      {/* Map Action */}
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ab-venue-cta"
                      >
                        <span>View Location on Maps</span>
                        <span className="ab-venue-arrow">→</span>
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── 8. Luxury Call-to-Action ── */}
        <section className="ab-cta-sec">
          <div className="ab-container-narrow">
            <h2 className="ab-cta-title">Begin Planning Your Celebration</h2>
            <p className="ab-cta-desc">
              Reserve your private theatre experience today or speak with our celebration coordinators to personalize every detail.
            </p>

            <div className="ab-cta-btns">
              <button
                type="button"
                className="ab-btn-primary"
                onClick={() => navigate("/locations")}
              >
                <span>Book This Experience</span>
                <span>→</span>
              </button>

              <a
                href="https://wa.me/918374777834"
                target="_blank"
                rel="noopener noreferrer"
                className="ab-btn-secondary"
              >
                <span>Chat on WhatsApp</span>
                <span>↗</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
